import { useAuth } from '@agentnet/auth';
import { createStyles, Grid, makeStyles, Theme } from '@material-ui/core';
import { getEndorsementsByPolicyDate } from 'api/jacket-api';
import { isValid, parse } from 'date-fns';
import Endorsements from 'features/jackets/Endorsements';
import IncorporatedEndorsements from 'features/jackets/IncorporatedEndorsement';
import { Endorsement } from 'features/jackets/types';
import { ReactNode, useEffect, useState } from 'react';
import AgentNetDivider from 'ui-kit/components/dividers/AgentNetDivider2';
import QuestionItem from 'ui-kit/components/tables/AdditionalQuestionItem';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    heading: {
      marginBottom: '16px',
      marginTop: '8px',
    },
    marginZero: {
      margin: 0,
    },
    paddingTopZero: {
      paddingTop: '0px !important',
    },
    endorsement: {
      padding: '16px 8px 8px 8px',
    },
    incorporatedEndorsements: {
      paddingTop: '16px',
      paddingLeft: '10px',
    },
  }),
);

interface IPolicy {
  callbacks: {
    updateCalcCriteriaData: any;
    updateJacketEndorsement: (
      jacketId: string | null,
      randomId: string | null,
      rflId: number | null,
      name: string | null,
      amount: number | null,
      reason: string,
      type: string,
      starsNumber: string | null,
      index: number,
    ) => void;
    renderFieldComponent: (
      field: any,
      type: string,
      validateErrors?: any,
      index?: any,
      subIndex?: any,
      filterRateType?: boolean,
    ) => ReactNode;
    openStatCodePDF: () => void;
    additionalQuestionRef: any;
  };
  disableFields: boolean;
  index: number;
  endorsementsList: Endorsement[];
  validationErrors: any;
  shouldShowValidation: boolean;
  isReissue: boolean;
  item: any;
  propertyState: string;
  rateEffectiveDate: string;
  underwriterCode: string;
}
const Policy = ({
  callbacks,
  isReissue,
  item,
  index,
  endorsementsList,
  propertyState,
  validationErrors,
  shouldShowValidation,
  disableFields,
  rateEffectiveDate,
  underwriterCode,
}: IPolicy) => {
  const { getAccessToken } = useAuth();
  const [filteredEndorsements, setFilteredEndorsements] = useState<Endorsement[]>([]);
  const [firstLoad, setFirstLoad] = useState(false);
  const [endorsementType, setEndorsementType] = useState<Endorsement[]>([]);
  const [policyDate, setPolicyDate] = useState<string>();
  const [isPolicyDateChanged, setIsPolicyDateChanged] = useState<boolean>();
  const [isEndorsementListLoaded, setIsEndorsementListLoaded] = useState<boolean>();

  const {
    updateCalcCriteriaData,
    renderFieldComponent,
    updateJacketEndorsement,
    openStatCodePDF,
    additionalQuestionRef,
  } = callbacks;
  const { policyType, policyNumber, fields, endorsements, incorporatedEndorsements, questions } = item;
  const policyRow = ['RateType', 'PolicyDate', 'PolicyAmount', 'StatCode'];
  const statCodeState = ['TX', 'NM', 'NY'];
  const classes = useStyles();
  const IsExtendedCoverage =
    fields.filter((field: any) => field.name === 'IsExtendedCoverage')?.[0]?.isChecked ?? false;

  const filterEndorsements = (): void => {
    if (firstLoad) {
      const policyCategory = item?.policyCategory ?? 'All';
      const policyCoverage = IsExtendedCoverage ? item?.extendedPolicyCoverage ?? 'All' : item?.policyCoverage ?? 'All';
      const filteredEndorsements = endorsementType.filter(
        (x: any) =>
          x.endorsementFormNumber === 'Miscellaneous Endorsement' ||
          ((x.policyCategories.includes('All') || x.policyCategories.includes(policyCategory)) &&
            (x.policyCoverages.includes('All') || x.policyCoverages.includes(policyCoverage))),
      );
      setFilteredEndorsements(filteredEndorsements);
    } else {
      setFirstLoad(true);
    }
  };

  const fetchEndorsements = async (stateCode?: string, underwriterCode?: string, effectiveDate?: string) => {
    const token = await getAccessToken();
    const endorsements = await getEndorsementsByPolicyDate(
      stateCode ?? '',
      underwriterCode ?? '',
      effectiveDate ?? '',
      token,
    );
    const miscEndorsement: Endorsement = {
      endorsementName: 'Miscellaneous Endorsement',
      endorsementFormNumber: 'Miscellaneous Endorsement',
      policyCategories: [],
      policyCoverages: [],
    };
    const allEndorsements = [...endorsements, miscEndorsement];
    setEndorsementType(allEndorsements);
  };

  const onChangeIncorporatedEndorsement = (
    randomId: string | null,
    endoId: string | null,
    endorsementFormNumber: string | null,
    reason: string | null,
    value: string | null,
  ) => {
    updateJacketEndorsement(
      null,
      randomId,
      null,
      value,
      null,
      reason as string,
      'incorporatedEndorsements',
      endorsementFormNumber,
      index,
    );
  };

  useEffect(() => {
    if (firstLoad) {
      filterEndorsements();
    }
  }, [IsExtendedCoverage, firstLoad]);

  useEffect(() => {
    if (endorsementsList) {
      setEndorsementType(endorsementsList);
    }
  }, [endorsementsList]);

  useEffect(() => {
    if (endorsementType) {
      filterEndorsements();
      // Notify that endorsements list is loaded
      setIsEndorsementListLoaded(true);
    }
  }, [endorsementType]);
  useEffect(() => {
    if (isPolicyDateChanged) {
      const currentPolicyDate = policyDate && isValid(parse(policyDate, 'MM/dd/yyyy', new Date())) ? policyDate : '';
      fetchEndorsements(propertyState, underwriterCode, currentPolicyDate);
      filterEndorsements();
      setIsPolicyDateChanged(false);
    }
  }, [isPolicyDateChanged]);

  useEffect(() => {
    // Let's wait for the parent component to load the endorsements list first
    // rateEffectiveDate takes precedence so only reload the endorsements if the rateEffectiveDate is not set
    // If it is set, then we use the list of endorsements that was already loaded with the rateEffectiveDate
    if (isEndorsementListLoaded && (rateEffectiveDate ?? '') === '') {
      if (item.fields?.length > 0) {
        const policyDateField = item.fields.find((field: any) => field.name === 'PolicyDate');
        if (policyDateField) {
          if (policyDate) {
            if (policyDate !== policyDateField.value) {
              setPolicyDate(policyDateField.value);
              setIsPolicyDateChanged(true);
            }
          } else {
            setPolicyDate(policyDateField.value);

            // When first loading the policy without rateEffectiveDate, then reload the endorsements when there is a policy date
            if ((policyDateField.value ?? '') !== '') {
              setIsPolicyDateChanged(true);
            }
          }
        }
      }
    }
  }, [item.fields, isEndorsementListLoaded]);

  return (
    <>
      <Grid item sm={12} className={classes.heading}>
        <AgentNetDivider
          typoVariant="h3"
          title={policyType}
          subtitle={policyNumber && `Policy No: ${policyNumber}`}
          subtitleMuted
          disablePadding
        />
      </Grid>
      <Grid container spacing={2} className={classes.marginZero}>
        {fields?.length > 0 &&
          fields?.map((field: any, subIndex: any) => (
            <>
              {field.dataType === 'checkbox' &&
                renderFieldComponent(field, 'updatePolicySettings', validationErrors, index, subIndex)}
            </>
          ))}
      </Grid>
      <Grid container spacing={2} className={classes.marginZero}>
        {fields?.length > 0 &&
          fields?.map((field: any, subIndex: any) => (
            <>
              {field.dataType !== 'checkbox' &&
                policyRow.includes(field.name) &&
                renderFieldComponent(
                  field,
                  'updatePolicySettings',
                  validationErrors,
                  index,
                  subIndex,
                  field.name === 'RateType' && IsExtendedCoverage,
                )}
            </>
          ))}
      </Grid>
      {isReissue && (
        <Grid container spacing={2} className={classes.marginZero}>
          {fields?.length > 0 &&
            fields?.map((field: any, subIndex: any) => (
              <>
                {field.dataType !== 'checkbox' &&
                  !policyRow.includes(field.name) &&
                  renderFieldComponent(field, 'updatePolicySettings', validationErrors, index, subIndex)}
              </>
            ))}
        </Grid>
      )}

      {incorporatedEndorsements?.length > 0 && (
        <Grid container spacing={2} className={classes.incorporatedEndorsements}>
          <IncorporatedEndorsements
            contentDisabled={disableFields}
            endorsements={incorporatedEndorsements}
            isFromJacket={false}
            showStatCode={statCodeState.includes(propertyState)}
            onChangeIncorporatedEndorsement={onChangeIncorporatedEndorsement}
          />
        </Grid>
      )}
      {endorsements && (
        <Grid container className={classes.endorsement}>
          <Endorsements
            isEditDisabled={disableFields}
            onChangeEndorsement={(...args) => updateJacketEndorsement(...args, index)}
            endorsements={endorsements}
            filterEndorsements={filterEndorsements}
            endorsementOptions={filteredEndorsements}
            validationErrors={validationErrors}
            openStatCodePDF={openStatCodePDF}
            shouldShowValidation={shouldShowValidation}
            shouldDisableScroll
            showStatCode={statCodeState.includes(propertyState)}
          />
        </Grid>
      )}
      {questions?.length > 0 && (
        <Grid container spacing={2} className={classes.marginZero}>
          <Grid item sm={12} className={classes.heading}>
            <AgentNetDivider typoVariant="h3" title={'Additional Questions'} disablePadding />
          </Grid>
          <Grid item sm={12}>
            {item?.questions.map((question: any, subindex: number) => (
              <QuestionItem
                {...question}
                dataTestId={`Policy${index}AdditionalQuestion${subindex}`}
                isDisabled={disableFields || question?.isDisabled}
                additionalQuestionRef={additionalQuestionRef}
                key={question.randomId}
                validationErrors={validationErrors}
                shouldShowValidation={shouldShowValidation}
                onChange={(val: any) => {
                  updateCalcCriteriaData('updateQuestions', 'jacketRates', val, index, subindex);
                }}
              />
            ))}
          </Grid>
        </Grid>
      )}
    </>
  );
};

export default Policy;
